import React, { useState, useEffect } from "react";
import logo from "../../../assets/Logo/logo.png";
import { useNavigate } from 'react-router-dom';

interface HeaderSectionProps {
    onPaketClick: () => void;
    onContactClick: () => void;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({onPaketClick, onContactClick}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isAboutMenuOpen, setIsAboutMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const navigate = useNavigate();

    // Add scroll event listener
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const toggleAboutMenu = () => {
        setIsAboutMenuOpen(!isAboutMenuOpen);
    };

    const handleBerandaButton = () => {
        navigate('/');
        setIsMenuOpen(!isMenuOpen);
        setIsAboutMenuOpen(!isAboutMenuOpen);
    };

    const handleToCompanyPage = () => {
        navigate('/company');
        setIsMenuOpen(!isMenuOpen);
        setIsAboutMenuOpen(!isAboutMenuOpen);
    }

    const handleToCareerPage = () => {
        navigate('/career');
        setIsMenuOpen(!isMenuOpen);
        setIsAboutMenuOpen(!isAboutMenuOpen);
    }

    const handleLogoClick = () => {
        navigate('/');
        // Close both menus if they're open
        if (isMenuOpen) setIsMenuOpen(false);
        if (isAboutMenuOpen) setIsAboutMenuOpen(false);
    };

    const handleEventPage = () => {
        navigate("/listnews");
    }

    const handleSMIPolicy = () => {
        navigate("/smi");
    }

    // Determine header background color based on both scroll and menu state
    const headerBackground = isMenuOpen || isScrolled ? 'bg-white shadow-md' : 'bg-transparent';
    const textColor = isMenuOpen || isScrolled ? 'text-gray-700' : 'text-white';

    return (
        <div className="fixed top-0 left-0 right-0 z-50">
            <header className={`flex items-center justify-between px-3 md:px-20 h-20 transition-all duration-300 ${headerBackground}`}>
                {/* Logo Section */}
                <div className="flex items-center gap-2">
                    <img src={logo} alt="Logo" className="w-20 h-7 md:w-40 md:h-14 cursor-pointer" onClick={handleLogoClick} />
                </div>

                {/* Mobile Menu Icon */}
                <div className="md:hidden flex items-center space-x-3">
                    {/* Menu Button */}
                    <button
                        onClick={toggleMenu}
                        className={`transition-colors duration-300 text-gray-700`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden md:flex flex-grow justify-end md:justify-end md:mr-4">
                    <ul className={`flex gap-4 md:gap-8 lg:gap-12 text-sm md:text-base font-bold transition-colors duration-300 ${textColor}`}>
                        <li className="text-black cursor-pointer font-poppins" onClick={handleBerandaButton}>Beranda</li>
                        <li className="text-black cursor-pointer font-poppins" onClick={onPaketClick}>Paket</li>
                        <li className="relative group text-black cursor-pointer font-poppins">
                            Tentang Kami
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-4 w-4 inline-block ml-2 ${isAboutMenuOpen ? "transform rotate-360" : ""}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            {/* Dropdown Menu */}
                            <ul className="absolute hidden group-hover:block top-full left-0 bg-white shadow-md rounded-md text-sm text-gray-700">
                                <li className="pr-4 py-2 px-2 hover:bg-gray-100 cursor-pointer text-left font-poppins" onClick={handleToCompanyPage}>Perusahaan</li>
                                <li className="pr-4 py-2 px-2 hover:bg-gray-100 cursor-pointer text-left font-poppins" onClick={handleToCareerPage}>Karir</li>
                                <li className="pr-4 py-2 px-2 hover:bg-gray-100 cursor-pointer text-left font-poppins" onClick={handleEventPage}>Berita dan Acara</li>
                                <li className="pr-4 py-2 px-2 hover:bg-gray-100 cursor-pointer text-left font-poppins" onClick={handleSMIPolicy}>Kebijakan SMI</li>
                            </ul>
                        </li>
                        <li className="text-black cursor-pointer font-poppins" onClick={onContactClick}>Kontak</li>
                    </ul>
                </nav>
            </header>

            {/* Mobile Navigation Drawer */}
            {isMenuOpen && (
                <div className="fixed inset-0 bg-white/95 backdrop-blur-sm md:hidden" style={{ top: '80px' }}>
                    <nav className="h-full overflow-y-auto">
                        <div className="px-4 py-6">
                            <ul className="space-y-4">
                                <li className="p-3 hover:bg-gray-50 rounded-lg transition-colors font-poppins" onClick={handleBerandaButton}>
                                    Beranda
                                </li>
                                <li className="p-3 hover:bg-gray-50 rounded-lg transition-colors font-poppins" onClick={onPaketClick}>
                                    Paket
                                </li>
                                <li className="font-poppins">
                                    <button 
                                        onClick={toggleAboutMenu}
                                        className="w-full p-3 hover:bg-gray-50 rounded-lg transition-colors flex justify-between items-center"
                                    >
                                        <span>Tentang Kami</span>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className={`h-4 w-4 transition-transform duration-200 ${isAboutMenuOpen ? 'rotate-180' : ''}`}
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    {isAboutMenuOpen && (
                                        <ul className="mt-2 pl-4">
                                            <li className="p-3 hover:bg-gray-50 rounded-lg transition-colors font-poppins" onClick={handleToCompanyPage}>
                                                Perusahaan
                                            </li>
                                            <li className="p-3 hover:bg-gray-50 rounded-lg transition-colors font-poppins" onClick={handleToCareerPage}>
                                                Karir
                                            </li>
                                            <li className="p-3 hover:bg-gray-50 rounded-lg transition-colors font-poppins" onClick={handleEventPage}>
                                                Berita dan Acara
                                            </li>
                                            <li className="p-3 hover:bg-gray-50 rounded-lg transition-colors font-poppins" onClick={handleSMIPolicy}>
                                                Kebijakan SMI
                                            </li>
                                        </ul>
                                    )}
                                </li>
                                <li className="p-3 hover:bg-gray-50 rounded-lg transition-colors font-poppins" onClick={onContactClick}>
                                    Kontak
                                </li>
                            </ul>
                        </div>
                    </nav>
                </div>
            )}
        </div>
    );
};

export default HeaderSection;